import { useEffect, useState } from 'react'
import type { DayQuestion, QuestionResult } from '../lib/types'
import { formatBig, formatExact, tierFor, missBadge, ordersOff } from '../lib/scoring'
import { haptic } from '../lib/share'

type Beat = 'freeze' | 'shrink' | 'zoom' | 'punch'

/**
 * The four beats: freeze → shrink → zoom (distance ∝ how wrong you were) → punch in the truth.
 * Reports the zoom factor up so the starfield can move with it.
 */
export function Reveal({ q, r, onZoom, onNext, isLast, liveNow, qIndex, qTotal }: {
  q: DayQuestion; r: QuestionResult; onZoom: (z: number) => void; onNext: () => void; isLast: boolean; liveNow?: number | null; qIndex?: number; qTotal?: number
}) {
  const [beat, setBeat] = useState<Beat>('freeze')
  const [factor, setFactor] = useState(1)
  const timedOut = !!r.timedOut
  const [count, setCount] = useState(0)
  const orders = timedOut ? 6 : ordersOff(r.ratio) // 0 = nailed, 7 = vertiginous
  const zoomAmt = 1 + Math.min(orders, 8) * 1.4 // how far the canvas recedes
  const tier = tierFor(r.points)

  useEffect(() => {
    const t: number[] = []
    t.push(window.setTimeout(() => setBeat('shrink'), 700))
    const zoomMs = 500 + Math.min(orders, 8) * 160
    t.push(window.setTimeout(() => {
      setBeat('zoom'); onZoom(zoomAmt); if (r.points === 0) haptic(40)
      if (!timedOut && r.ratio > 1.05) {
        // the distance counter: ×1 → ×(how wrong you were), log-interpolated with the zoom
        const start = performance.now()
        const tick = (now: number) => {
          const p = Math.min(1, (now - start) / zoomMs)
          setFactor(Math.pow(r.ratio, p))
          if (p < 1) requestAnimationFrame(tick)
        }
        requestAnimationFrame(tick)
        t.push(window.setTimeout(() => setFactor(r.ratio), zoomMs + 60)) // hidden-tab rAF fallback
      }
    }, 1300))
    t.push(window.setTimeout(() => {
      setBeat('punch'); onZoom(1); haptic(20)
      const start = performance.now(), dur = 900
      const tick = (now: number) => {
        const p = Math.min(1, (now - start) / dur), e = 1 - Math.pow(1 - p, 3)
        setCount(r.answer * e)
        if (p < 1) requestAnimationFrame(tick)
      }
      requestAnimationFrame(tick)
      t.push(window.setTimeout(() => setCount(r.answer), dur + 80)) // rAF is paused in hidden tabs
    }, 1300 + zoomMs))
    return () => t.forEach(clearTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const guessCls = 'guess' + (beat === 'shrink' || beat === 'zoom' ? ' shrunk' : '') + (beat === 'punch' ? ' gone' : '')
  return (
    <div className="reveal">
      <div className={'flash' + (beat === 'punch' ? ' go' : '')} />
      {beat !== 'punch' && (
        <>
          <div className="label" style={{ opacity: beat === 'freeze' ? 1 : 0 }}>{timedOut ? 'time ran out' : 'your guess'}</div>
          <div className={guessCls}>{timedOut ? '⏰' : formatBig(r.guess)}</div>
          {beat === 'zoom' && !timedOut && r.ratio > 1.05 && (
            <div className="zoom-factor">
              <b>×{factor >= 100 ? Math.round(factor).toLocaleString('en-US') : factor.toFixed(1)}</b>
              <span>away from the truth</span>
            </div>
          )}
        </>
      )}
      {beat === 'punch' && (
        <>
          <div className="label">the truth</div>
          <div className="truth">{formatBig(count)}</div>
          <div className="exact">{formatExact(r.answer)} {q.unit}</div>
          <div className="verdict">
            <span className="em">{timedOut ? '⏰' : tier.emoji}</span>
            {!timedOut && <span className="your-guess">Your guess: <b>{formatBig(r.guess)}</b></span>}
            <span>{timedOut ? 'TIMEOUT' : tier.name} · <b className="pts">+{r.qscore.toLocaleString('en-US')} pts</b></span>
            <span className="miss">
              {timedOut
                ? <span className="miss-badge high">no guess before the clock hit zero</span>
                : <span className={'miss-badge ' + (r.guess === r.answer ? 'spot' : r.guess > r.answer ? 'high' : 'low')}>{missBadge(r.guess, r.answer)}</span>}
            </span>
          </div>
          <p className="story">{q.story}<small><span className="src">SOURCE</span> {q.credit}</small></p>
          {liveNow != null && liveNow !== r.answer && (
            <div className="moved">it was {formatExact(r.answer)} at the freeze — it's {formatExact(liveNow)} as you read this</div>
          )}
          <button className="btn next" onClick={onNext}>{isLast ? 'See my score →' : 'Next →'}</button>
          {qTotal != null && qIndex != null && (
            <div className="reveal-stepper" aria-label={`question ${qIndex + 1} of ${qTotal}`}>
              {Array.from({ length: qTotal }, (_, k) => <i key={k} className={k < qIndex ? 'done' : k === qIndex ? 'cur' : ''} />)}
            </div>
          )}
        </>
      )}
    </div>
  )
}
